import * as React from "react";
import "./index.scss";
import Page from "./components/Page";
import CoverPage from "./components/CoverPage";
import qs from "qs";
import axios from "axios";
import {API, INTERNAL_API} from "../../utilities/api.config";
import SummarySection from "./components/SummarySection";
import StrategyPerformanceStats from "./components/StrategyPerformanceStats";
import { generateChartData } from "../../utilities/cpr";
import StrategyPerformanceChart from "./components/StrategyPerformanceChart";
import StrategyRiskMetrics from "./components/StrategyRiskMetrics";
import StrategyReturnMetrics from "./components/StrategyReturnMetrics";
import StrategyCalendarYearReturn from "./components/StrategyCalendarYearReturn";
import ClientProfile from "./components/ClientProfile";
import ModelConstDisclosureSection from "./components/ModelConstDisclosureSection";
import TableOfContents from "./components/TableOfContents";
import DisclaimersAndDisclosuresSection from "./components/DisclaimersAndDisclosuresSection";
import PortfolioFeeSummarySection from "./components/PortfolioFeeSummarySection";
import { ChartColors } from "../../theme";
import withStyles from "@material-ui/core/styles/withStyles";
import {connect} from "react-redux";
import StrategyMonteCarloChart from "./components/StrategyMonteCarloChart";
import {RESTRICTED_BROKERS} from "../../utilities/constant";
import { isBasicView } from "../../utilities/access";
import moment from "moment";

class SummaryPDF extends React.Component {
  state = {
    advisor: null,
    accessLevel: 0,
    portfolio: null,
    custodians: null,
    fees: null,
    householdId: null,
    portfolioId: null,
    token: null,
    includeModelConstituentDisclosure: "1",
    custodianId: null
  };

  componentDidMount() {
    const { location, theme } = this.props;
    const parsedQueryString = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    const {
      householdId,
      portfolioId,
      token,
      nonDraft,
      includeModelConstituentDisclosure,
      devMode,
      custodianId
    } = parsedQueryString;
    this.setState({
      householdId,
      portfolioId,
      token,
      includeModelConstituentDisclosure,
      custodianId: custodianId ? Number(custodianId) : null
    });

    if (token && householdId) {
      const API_URL = devMode === '1' ? API : INTERNAL_API;

      axios.all([
        axios.get(`${API_URL.GET_CUSTOM_CONFIGURATION_BY_CLIENT_ID}`),
        axios
          .get(
            `${
              API_URL.GET_PORTFOLIO_FOR_PRINT
            }?householdId=${householdId}&portfolioId=${
              portfolioId ? portfolioId : ""
            }`,
            { headers: token ? { "X-WBI-Auth": token } : {} })
      ]).then(axios.spread((...response) => {
        // Adding this to the redux for palette usage only
        // Because palette is part from the MuiThemeProvider
        this.props.dispatch({
          type: "SET_APP_CUSTOM_CONFIGURATION_FOR_PDF_PAGE",
          payload: {
            defaultTheme: response[0].data,
            customizedTheme: response[1].data.data.advisorCustomization
          },
        });
        this.setState(
          {
            portfolio:
              nonDraft === "1"
                ? { ...response[1].data.data.portfolio, onboardStatus: "" }
                : response[1].data.data.portfolio,
            custodians: response[1].data.data.custodians,
            fees: response[1].data.data.fees,
            advisor: response[1].data.data.advisor,
            accessLevel: response[1].data.data.advisor.userRole.accessLevel,
          },
          () => {
            document.body.style.backgroundColor = theme.palette.common.white;
            this.hideEmptyModelDisclosureTables();
            this.assignPageNumbers();
            this.draftTableOfContent();
          }
        );
      }))
    }
  }

  assignPageNumbers = () => {
    const totalPages = document.querySelectorAll(".page").length;
    document.querySelectorAll("span.totalPages").forEach((node) => {
      node.innerText = totalPages;
    });
    document.querySelectorAll("span.pageIndex").forEach((node, index) => {
      node.innerText = index + 2;
    });
  };

  hideEmptyModelDisclosureTables = () => {
    document.querySelectorAll("table.model-constituent").forEach((node) => {
      if (node.rows.length === 1) {
        node.style.display = "none";
      }
    });
  };

  draftTableOfContent = () => {
    const nodes = Array.from(
      document.querySelectorAll(".content, .sub-content")
    ).filter((node) => node.getAttribute("data-toc"));
    const contentItems = nodes.map((node) => {
      const pageTitle = node.getAttribute("data-toc");
      const pageIndex = node
        .closest(".page")
        .querySelector(".pageIndex").innerText;
      return {
        indentation: node.classList.contains("content") ? 0 : 1,
        pageTitle,
        pageIndex,
      };
    });
    let tableDom = "";
    contentItems.forEach((item) => {
      tableDom += `<li>
        <span class="MuiTypography-root ${
          item.indentation === 0 ? "MuiTypography-h5" : "MuiTypography-h6"
        }">${item.pageTitle}</span>
        <span class="MuiTypography-root ${
          item.indentation === 0 ? "MuiTypography-h5" : "MuiTypography-h6"
        } page-number">${item.pageIndex}</span>
      </li>`;
    });
    document.querySelector(".table-of-contents").innerHTML = tableDom;
  };

  render() {
    const { portfolio, custodians, fees, includeModelConstituentDisclosure, custodianId, advisor, accessLevel } = this.state;
    const appState = this.props.app;
    if (!portfolio || !portfolio.cprRecommendation || !custodians || !fees || !appState || !advisor) {
      return null;
    }

    const { metadata } = portfolio;

    // On Feb 22, 2024, all users are seeing cambridge version
    // const bdIsCambridge = RESTRICTED_BROKERS.includes(advisor.brokerDealer.name);
    const bdIsCambridge = true;

    const baseChartData = generateChartData(portfolio);
    if (!baseChartData) {
      return null;
    }

    const annualWithdrawalChartData = metadata.annualWithdrawal
      ? generateChartData(portfolio, metadata.annualWithdrawal)
      : null;
    const totalWithdrawal = metadata.annualWithdrawal
      ? generateChartData(portfolio, metadata.annualWithdrawal, true)
      : null;
    const benchmarkName = portfolio.cprRecommendation.summary.benchmark.Name;
    const containsCalendarYearReturnData = portfolio.cprRecommendation.calendarYearReturn &&
      portfolio.cprRecommendation.calendarYearReturn.startYear &&
      portfolio.cprRecommendation.calendarYearReturn.CprCalYearReturn &&
      portfolio.cprRecommendation.calendarYearReturn.BenchmarkCalYearReturn;
    const userIsApproved = portfolio.advisors.every(_ => _.approved);
    const strategyStartDate = moment(baseChartData[0].date)
      .startOf("month")
      .subtract(2, "months");

    const { classes } = this.props;

    return (
      <div className={"print-body-container " + classes.root}>
        <table>
          <tbody>
            <tr>
              <td>
                {/*Cover page*/}
                <CoverPage portfolio={portfolio} app={appState} />

                {/*Table of Content page*/}
                <Page portfolio={portfolio}>
                  <TableOfContents />
                </Page>

                {/*Summary page*/}
                <Page portfolio={portfolio}>
                  <SummarySection
                    portfolio={portfolio}
                    baseChartData={baseChartData}
                    benchmarkName={benchmarkName}
                    accessLevel={accessLevel}
                  />
                </Page>

                {/*Strategy Perf stats*/}
                <Page portfolio={portfolio}>
                  <StrategyPerformanceStats
                    portfolio={portfolio}
                    baseChartData={baseChartData}
                    annualWithdrawalChartData={annualWithdrawalChartData}
                    totalWithdrawal={totalWithdrawal}
                    bdIsCambridge={bdIsCambridge}
                    accessLevel={accessLevel}
                  />
                </Page>

                {/*Strategy implementation*/}
                {/* <Page portfolio={portfolio}>
                  <StrategyImplementation
                    portfolio={portfolio}
                    baseChartData={baseChartData}
                    annualWithdrawalChartData={annualWithdrawalChartData}
                    benchmarkName={benchmarkName}
                  />
                </Page> */}

                {/*Charts*/}
                {
                  (!bdIsCambridge && !isBasicView(accessLevel)) &&
                  <Page portfolio={portfolio}>
                    <StrategyPerformanceChart
                      portfolio={portfolio}
                      baseChartData={baseChartData}
                      benchmarkName={benchmarkName}
                    />
                  </Page>
                }


                {/*Charts with annual withdrawal*/}
                {
                  (!bdIsCambridge && !isBasicView(accessLevel)) &&
                  metadata.annualWithdrawal ? (
                  <Page portfolio={portfolio}>
                    <StrategyPerformanceChart
                      portfolio={portfolio}
                      totalWithdrawal={totalWithdrawal}
                      annualWithdrawalChartData={annualWithdrawalChartData}
                      benchmarkName={benchmarkName}
                    />
                  </Page>
                ): null }

                <Page portfolio={portfolio}>
                  <StrategyMonteCarloChart
                    portfolio={portfolio}
                    startDate={strategyStartDate}
                  />
                </Page>

                {/*Risk Metrics*/}
                {
                  <Page portfolio={portfolio}>
                    <StrategyRiskMetrics
                      portfolio={portfolio}
                      baseChartData={baseChartData}
                      bdIsCambridge={bdIsCambridge}
                      accessLevel={accessLevel}
                    />
                  </Page>
                }

                {/*Return Metrics */}
                <Page portfolio={portfolio}>
                  <StrategyReturnMetrics
                    portfolio={portfolio}
                    baseChartData={baseChartData}
                    bdIsCambridge={bdIsCambridge}
                    accessLevel={accessLevel}
                  />
                </Page>

                {/*Calendar year Return */}
                {
                  (!bdIsCambridge && !isBasicView(accessLevel)) &&
                  containsCalendarYearReturnData ? (
                    <Page portfolio={portfolio}>
                      <StrategyCalendarYearReturn
                        portfolio={portfolio}
                        benchmarkName={benchmarkName}
                      />
                    </Page>
                ) : null }

                {/*Client Profile*/}
                <Page portfolio={portfolio}>
                  <ClientProfile portfolio={portfolio} />
                </Page>

                {/*Fee summary*/}
                {
                  !appState.hideFeeSchedule && (
                    <Page portfolio={portfolio}>
                      <PortfolioFeeSummarySection
                        appName={appState.appName}
                        portfolio={portfolio}
                        custodians={custodians}
                        fees={fees}
                        custodianId={custodianId}
                        accessLevel={accessLevel}
                      />
                    </Page>
                  )
                }

                {/*Model Constituent Disclosure Section Pages*/}
                {
                  includeModelConstituentDisclosure === "0"
                    ? null :
                    (
                      <ModelConstDisclosureSection
                        portfolio={portfolio}
                        cprRecommendation={portfolio.cprRecommendation}
                        userIsApproved={userIsApproved}
                      />
                    )
                }

                {/*Appendices Cover Page*/}
                {/*<AppendicesCoverPage portfolio={portfolio}/>*/}

                <DisclaimersAndDisclosuresSection
                  portfolio={portfolio}
                  app={appState}
                  data={
                    {
                      "MC_PORTFOLIO_RETURN": parseFloat(portfolio.cprRecommendation.summary.recommendationSummary.AnnualizedReturn),
                      "MC_STANDARD_DEVIATION": parseFloat(portfolio.cprRecommendation.summary.recommendationSummary.AnnualizedStandardDeviation),
                      "MC_START_DATE": portfolio.cprRecommendation.chartData.startDate
                    }
                  }
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}



const styles = (theme) => {
  const colors = ChartColors();
  const main = colors.next();
  // console.log(main);
  return {
    'root': {
      '& .page table .MuiTableCell-head': {
        color: main.value
      }
    }
  } 
};

const mapStateToProps = (state) => {
  // Use Redux solely for rendering the correct color palette.
  // Other data are rendered from appState
  return {
    app: state.app,
  };
};

export default withStyles(styles, { withTheme: true })(connect(mapStateToProps)(SummaryPDF));



// WEBPACK FOOTER //
// ./src/containers/SummaryPDF/index.jsx